import { useMemo } from 'react';

import { useShowLocation, useShowMerchant } from '@givebacks/api/offers';
import {
  Affix,
  Anchor,
  Badge,
  BadgeProps,
  ButtonMantine,
  DarkModalHeader,
  Group,
  Modal,
  Skeleton,
  Stack,
  Text,
  Title,
} from 'ui';

import { useMerchantDetailModalProvider } from '../MerchantDetailModalsProvider';
import { CashbackAvailableDays } from '../components/CashbackAvailableDays';
import { HoursOfOperation } from '../components/HoursOfOperation';
import { MerchantBlueInfoCard } from '../components/MerchantBlueInfoCard';
import {
  DiningDetailDescription,
  MerchantDetailDescription,
} from '../components/MerchantDetailDescription';

const MERCHANT_BADGE_MAP = new Map<string, BadgeProps>([
  ['online', { color: 'blue', children: 'Online Offer' }],
  ['instore', { color: 'mint', children: 'In-Store Offer' }],
  ['dining', { color: 'mint', children: 'Dining Offer' }],
]);

const MerchantDetailModalHeader = () => {
  const { meta } = useMerchantDetailModalProvider({});
  const { merchantId, locationId, type } = meta;

  const { data: merchantData, isLoading: isFetchingMerchant } = useShowMerchant({
    merchantId: merchantId ?? '',
  });
  const { data: locationData, isLoading: isFetchingLocation } = useShowLocation({
    locationId: locationId ?? '',
  });

  const loading = isFetchingMerchant || isFetchingLocation;

  const addressString = useMemo(
    () =>
      `${locationData?.address1 ?? ''} ${locationData?.address2 ?? ''} ${
        locationData?.city + ','
      } ${locationData?.state ?? ''} ${locationData?.zip_code ?? ''}`,
    [
      locationData?.address1,
      locationData?.address2,
      locationData?.city,
      locationData?.state,
      locationData?.zip_code,
    ]
  );

  return (
    <DarkModalHeader causeImg={merchantData?.logo?.url} isLoading={loading}>
      {() => (
        <Stack gap={4}>
          <Text fz="lg" fw={600}>
            {merchantData?.name}
          </Text>
          <Text fz={11}>
            {type === 'online' ? `${merchantData?.domain} for great cashback deals` : addressString}
          </Text>
        </Stack>
      )}
    </DarkModalHeader>
  );
};

const MerchantDetailModalFooter = ({
  handleActivation,
  merchantId,
}: {
  handleActivation: (merchantId: string) => void;
  merchantId: string;
}) => {
  const { meta } = useMerchantDetailModalProvider({});

  const { type, locationId } = meta;

  const { data: locationData } = useShowLocation({
    locationId: locationId,
  });

  if (type === 'dining') return null;
  if (locationData?.merchant.activation?.active) return null;

  return (
    <Affix
      pos={'sticky'}
      withinPortal={false}
      bottom={0}
      left={0}
      pt="lg"
      bg={'linear-gradient(180deg, rgba(255, 255, 255, 0) 0%, #FFFFFF 35.23%)'}
    >
      <Stack>
        <ButtonMantine fullWidth onClick={() => handleActivation(merchantId)}>
          {type === 'online' ? 'Visit Site' : 'Claim Offer'}
        </ButtonMantine>
        <Text ta="center" fz={10} c="gray">
          Special Conditions: Cashback is not available on shipping or the purchase/redemption of
          gift cards.
        </Text>
      </Stack>
    </Affix>
  );
};

const MerchantDetailContent = ({
  handleActivation,
}: {
  handleActivation: (merchantId: string) => void;
}) => {
  const { openModalById, meta } = useMerchantDetailModalProvider({});

  const { merchantId, locationId, type } = meta;

  const { data: merchantData } = useShowMerchant({
    merchantId: merchantId,
  });
  const { data: locationData } = useShowLocation({
    locationId: locationId,
  });

  const addressString = useMemo(
    () =>
      `${locationData?.address1 ?? ''} ${locationData?.address2 ?? ''} ${
        locationData?.city + ','
      } ${locationData?.state ?? ''} ${locationData?.zip_code ?? ''}`,
    [
      locationData?.address1,
      locationData?.address2,
      locationData?.city,
      locationData?.state,
      locationData?.zip_code,
    ]
  );

  if (!merchantId) return;
  return (
    <Stack gap="lg" px="lg" pb="lg">
      <Stack gap="xs">
        <Group justify="space-between">
          <Badge
            {...MERCHANT_BADGE_MAP.get(type ?? '')}
            type={type === 'online' ? 'info' : 'success'}
            variant="filled"
          />
          {type !== 'online' && !!merchantData?.url && (
            <Anchor href={merchantData?.url} target={'_blank'}>
              <ButtonMantine variant="subtle" size="compact-sm">
                Visit Website
              </ButtonMantine>
            </Anchor>
          )}
        </Group>
        <Stack gap="sm">
          <Skeleton visible={!merchantData?.tagline}>
            <Title order={3}>
              {`Get ${merchantData?.tagline} when you pay with your connected card`}
            </Title>
          </Skeleton>
          <MerchantDetailDescription type={type} />
        </Stack>
      </Stack>

      <MerchantBlueInfoCard type={type} onClick={() => openModalById('Terms')} />
      {type === 'dining' && locationData && (
        <DiningDetailDescription
          text={locationData.offers[0].description}
          address={addressString}
        />
      )}
      {type === 'dining' && <CashbackAvailableDays locationId={locationId} />}
      <HoursOfOperation locationId={locationId} type={type} />
      <MerchantDetailModalFooter handleActivation={handleActivation} merchantId={merchantId} />
    </Stack>
  );
};

const MerchantDetailModal = ({ handleClick }: { handleClick: (merchantId: string) => void }) => {
  const { opened, close } = useMerchantDetailModalProvider({
    id: 'Detail',
  });

  return (
    <Modal
      opened={opened}
      onClose={() => {
        close();
      }}
      title={<MerchantDetailModalHeader />}
      styles={{ body: { padding: 0 } }}
    >
      <MerchantDetailContent handleActivation={handleClick} />
    </Modal>
  );
};

export { MerchantDetailModal };
